import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { Sun, Moon, X, Bell, Settings, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { WeekCalendar } from '../../components/user/WeekCalendar';
import { JournalCalendar, EntryDetailModal } from '../../components/user/JournalCalendar';

interface JournalEntry {
  id: string;
  promptType: 'morning' | 'evening';
  completedAt: Date;
  reflectionDetails?: Array<{
    question: string;
    answer: string;
  }>;
}

export const PromptedJournalNew: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<JournalEntry[]>([]);
  const [todaysMorning, setTodaysMorning] = useState<JournalEntry | null>(null);
  const [todaysEvening, setTodaysEvening] = useState<JournalEntry | null>(null);
  const [showFullCalendar, setShowFullCalendar] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');
  const currentHour = new Date().getHours();
  const isMorningTime = currentHour >= 6 && currentHour < 12;
  const isEveningTime = currentHour >= 17 && currentHour < 22;

  useEffect(() => {
    if (!user) return;
    fetchJournalData();
  }, [user]);

  const fetchJournalData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch prompted journals
      const entriesQuery = query(
        collection(db, 'users', user.uid, 'promptedJournals'),
        orderBy('completedAt', 'desc')
      );
      const entriesSnap = await getDocs(entriesQuery);
      const entriesData = entriesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate(),
      })) as JournalEntry[];
      
      setEntries(entriesData);

      // Check for today's entries
      const todayEntries = entriesData.filter(entry => 
        entry.completedAt && format(entry.completedAt, 'yyyy-MM-dd') === today
      );
      
      setTodaysMorning(todayEntries.find(e => e.promptType === 'morning') || null);
      setTodaysEvening(todayEntries.find(e => e.promptType === 'evening') || null);

    } catch (error) {
      console.error('Error fetching journal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date: Date, dateEntries: JournalEntry[]) => {
    setSelectedDate(date);
    setSelectedEntries(dateEntries);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Daily Reflections
          </h1>
          <p className="text-gray-600">
            Track your wellness journey with morning and evening check-ins
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Morning Reflection Card */}
          <Link
            to="/journal/morning"
            className="group relative overflow-hidden bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-yellow-200"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-yellow-500 to-orange-500 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <Sun className="w-7 h-7 text-white" />
                </div>
                {todaysMorning ? (
                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    ✓ Completed
                  </div>
                ) : isMorningTime ? (
                  <div className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                    Recommended
                  </div>
                ) : null}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Morning Check-in
              </h2>
              <p className="text-gray-600 mb-4">
                Prime your brain to focus on the good stuff
              </p>
              
              {todaysMorning ? (
                <div className="text-sm text-gray-500">
                  Completed at {format(todaysMorning.completedAt, 'h:mm a')}
                </div>
              ) : (
                <div className="text-yellow-600 font-medium group-hover:translate-x-1 transition-transform">
                  Begin reflection →
                </div>
              )}
            </div>
            
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-yellow-200/30 to-transparent rounded-tl-full" />
          </Link>

          {/* Evening Reflection Card */}
          <Link
            to="/journal/evening"
            className="group relative overflow-hidden bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-indigo-200"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gradient-to-br from-indigo-500 to-purple-500 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg">
                  <Moon className="w-7 h-7 text-white" />
                </div>
                {todaysEvening ? (
                  <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    ✓ Completed
                  </div>
                ) : isEveningTime ? (
                  <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                    Recommended
                  </div>
                ) : null}
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Evening Check-in
              </h2>
              <p className="text-gray-600 mb-4">
                End your day on a good note
              </p>
              
              {todaysEvening ? (
                <div className="text-sm text-gray-500">
                  Completed at {format(todaysEvening.completedAt, 'h:mm a')}
                </div>
              ) : (
                <div className="text-indigo-600 font-medium group-hover:translate-x-1 transition-transform">
                  Begin reflection →
                </div>
              )}
            </div>
            
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-indigo-200/30 to-transparent rounded-tl-full" />
          </Link>
        </div>

        {/* Notification Settings Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-4 sm:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-3 rounded-xl">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                  Journal Reminders
                </h3>
                <p className="text-sm text-gray-600">
                  Get notified to complete your Morning and Evening Reflections. Stay consistent with your wellness journey!
                </p>
              </div>
            </div>
            <Link
              to="/journal/notifications"
              className="inline-flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              <Settings className="w-4 h-4" />
              <span>Manage Notifications</span>
            </Link>
          </div>
        </div>

        {/* Week Calendar Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Reflection History
          </h2>
          <WeekCalendar
            entries={entries}
            onDateSelect={handleDateSelect}
            onShowFullCalendar={() => setShowFullCalendar(true)}
          />
        </div>

        {/* Recent Entries */}
        {entries.length > 0 && (
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6">
              Recent Reflections
            </h2>
            <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
              {entries.slice(0, 5).map(entry => (
                <button
                  key={entry.id}
                  onClick={() => handleDateSelect(entry.completedAt, [entry])}
                  className="w-full bg-white rounded-xl shadow hover:shadow-md lg:hover:shadow-lg transition-all p-4 lg:p-5 border border-gray-200 text-left lg:hover:scale-[1.02] lg:hover:border-gray-300"
                >
                  <div className="flex items-center gap-3 lg:gap-4">
                    {entry.promptType === 'morning' ? (
                      <div className="bg-yellow-100 p-2 lg:p-3 rounded-lg">
                        <Sun className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
                      </div>
                    ) : (
                      <div className="bg-indigo-100 p-2 lg:p-3 rounded-lg">
                        <Moon className="w-5 h-5 lg:w-6 lg:h-6 text-indigo-600" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 lg:text-lg">
                          {entry.promptType === 'morning' ? 'Morning' : 'Evening'} Reflection
                        </h3>
                        <span className="text-sm lg:text-base text-gray-500">
                          {format(entry.completedAt, 'MMM d, yyyy')} at {format(entry.completedAt, 'h:mm a')}
                        </span>
                      </div>
                      {entry.reflectionDetails && entry.reflectionDetails.length > 0 && (
                        <p className="text-sm lg:text-base text-gray-600 line-clamp-1 lg:line-clamp-2">
                          {entry.reflectionDetails[0].answer}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {entries.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gray-200 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No reflections yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start your wellness journey with your first morning or evening reflection
            </p>
          </div>
        )}
      </div>

      {/* Full Calendar Modal */}
      {showFullCalendar && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-xl font-bold text-gray-900">Full Calendar</h3>
              <button
                onClick={() => setShowFullCalendar(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              <JournalCalendar
                entries={entries}
                onDateSelect={(date, dateEntries) => {
                  handleDateSelect(date, dateEntries);
                  setShowFullCalendar(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Entry Detail Modal */}
      {selectedDate && selectedEntries.length > 0 && (
        <EntryDetailModal
          date={selectedDate}
          entries={selectedEntries}
          onClose={() => {
            setSelectedDate(null);
            setSelectedEntries([]);
          }}
        />
      )}
    </div>
  );
};

